import React, { useEffect, useId, useRef, useState } from "react";
import { FiUsers, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { fetchCoordinatorDashboard } from "../helpers/apiHelper";
import { cleanCoCoName } from "../helpers/cocoNameHelper";

export const COCO_OPTIONS_DEBOUNCE_MS = 300;

const fold = (value) => String(value ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

// Searchable single-select (combobox + listbox) over the coordinator
// dashboard CoCo list. Typing refines the list server-side (searchTerm).
const CoCounselSelect = ({ id, value, onChange, idToken, onSubmitRequest }) => {
  const { t } = useTranslation();
  const listboxId = `${useId()}-listbox`;
  const [text, setText] = useState(cleanCoCoName(value?.cocoName));
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setText(cleanCoCoName(value?.cocoName) || (value?.cocoId ? value.cocoId : ""));
  }, [value?.cocoId, value?.cocoName]);

  const selectedName = cleanCoCoName(value?.cocoName) || value?.cocoId || "";
  const term = open && text !== selectedName ? text.trim() : "";

  useEffect(() => {
    if (!open || !idToken) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(false);
      fetchCoordinatorDashboard(idToken, null, term)
        .then((data) => {
          if (cancelled) return;
          const list = Array.isArray(data?.cocos) ? data.cocos : [];
          const needle = fold(term);
          setOptions(list.filter((coco) => coco?.cocoId && fold(cleanCoCoName(coco.cocoName)).includes(needle)));
          setActiveIndex(-1);
        })
        .catch((requestError) => {
          if (cancelled) return;
          console.error("Failed to load CoCounsel options:", requestError);
          setError(true);
          setOptions([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, COCO_OPTIONS_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [idToken, open, term]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const select = (coco) => {
    onChange(coco ? { cocoId: coco.cocoId, cocoName: coco.cocoName || "" } : { cocoId: "", cocoName: "" });
    setText(cleanCoCoName(coco?.cocoName));
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleBlur = (event) => {
    if (wrapperRef.current?.contains(event.relatedTarget)) return;
    setOpen(false);
    if (!text.trim()) {
      if (value?.cocoId) select(null);
    } else {
      setText(selectedName);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (!options.length) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => (current + step + options.length) % options.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (open && activeIndex >= 0 && options[activeIndex]) {
        select(options[activeIndex]);
        return;
      }
      setOpen(false);
      onSubmitRequest?.();
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      setText(selectedName);
    }
  };

  const activeId = open && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined;

  return (
    <div className="adv-search-combobox" ref={wrapperRef}>
      <div className="adv-search-input-wrap">
        <FiUsers className="adv-search-input-icon" aria-hidden="true" />
        <input
          id={id}
          type="text"
          className="portal-input adv-search-input"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeId}
          autoComplete="off"
          placeholder={t("advancedSearchForm.placeholders.cocoId")}
          value={text}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
          }}
        />
        {value?.cocoId && (
          <button
            type="button"
            className="adv-search-clear-field"
            aria-label={t("advancedSearchForm.coco.clear")}
            title={t("advancedSearchForm.coco.clear")}
            onClick={() => select(null)}
          >
            <FiX aria-hidden="true" />
          </button>
        )}
      </div>
      {open && (
        <ul id={listboxId} role="listbox" className="adv-search-listbox" aria-label={t("advancedSearchForm.fields.cocoId")}>
          {loading && <li className="adv-search-listbox-note" role="presentation">{t("advancedSearchForm.coco.loading")}</li>}
          {!loading && error && <li className="adv-search-listbox-note is-error" role="presentation">{t("advancedSearchForm.coco.error")}</li>}
          {!loading && !error && options.length === 0 && (
            <li className="adv-search-listbox-note" role="presentation">{t("advancedSearchForm.coco.empty")}</li>
          )}
          {!loading && options.map((coco, index) => (
            <li
              key={coco.cocoId}
              id={`${listboxId}-${index}`}
              role="option"
              aria-selected={coco.cocoId === value?.cocoId}
              className={`adv-search-option${index === activeIndex ? " is-active" : ""}${coco.cocoId === value?.cocoId ? " is-selected" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => select(coco)}
            >
              <span>{cleanCoCoName(coco.cocoName) || coco.cocoId}</span>
              {Number.isFinite(coco.totalClients) && (
                <span className="adv-search-option-meta">{coco.totalClients}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CoCounselSelect;
